import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { createClient } from '@/lib/supabase-client'
import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string | null
  content: string
  cover_image_url: string | null
  tags: string[] | null
  published_at: string | null
  created_at: string | null
}

export default function BlogPage() {
  const supabase = createClient()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, slug, title, excerpt, content, cover_image_url, tags, published_at, created_at')
          .eq('is_published', true)
          .order('published_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })

        if (error) {
          throw error
        }

        setPosts(data || [])
      } catch (err: any) {
        console.error('Error loading blog posts', err)
        setError(err.message || 'Failed to load blog posts')
      } finally {
        setLoading(false)
      }
    }

    loadPosts()
  }, [supabase])

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    posts.forEach((post) => {
      post.tags?.forEach((tag) => {
        if (tag) {
          tagSet.add(tag)
        }
      })
    })
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b))
  }, [posts])

  const recentPosts = useMemo(
    () => posts.slice(0, 5),
    [posts]
  )

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            GistMag Blog
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Product updates, tutorials, and best practices for Gist Images, Gist Speech, and Gist Blog.
          </p>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 dark:border-primary-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
                Loading blog posts...
              </p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="max-w-xl mx-auto mb-8">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            </div>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="max-w-xl mx-auto mb-8">
            <div className="glass-card rounded-xl p-6 text-center">
              <h2 className="text-xl font-semibold mb-2">No posts yet</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                When new posts are published, they&apos;ll appear here.
              </p>
            </div>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <section className="space-y-6">
              {posts.map((post) => {
                const dateString = post.published_at || post.created_at
                const date = dateString ? new Date(dateString) : null

                return (
                  <article
                    key={post.id}
                    className="glass-card rounded-xl overflow-hidden hover-lift transition"
                  >
                    {post.cover_image_url && (
                      <Link to={`/blog/${post.slug}`}>
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          className="w-full h-56 object-cover"
                          loading="lazy"
                        />
                      </Link>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        {date && (
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {date.toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 justify-end">
                            {post.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <h2 className="text-2xl font-bold mb-2">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="hover:text-primary-600 dark:hover:text-primary-400 transition"
                        >
                          {post.title}
                        </Link>
                      </h2>

                      {post.excerpt && (
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                      )}

                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        Read more
                        <svg
                          className="w-4 h-4 ml-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    </div>
                  </article>
                )
              })}
            </section>

            <aside className="space-y-6">
              <section className="glass-card rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                  Recent posts
                </h3>
                <ul className="space-y-3">
                  {recentPosts.map((post) => {
                    const dateString = post.published_at || post.created_at
                    const date = dateString ? new Date(dateString) : null

                    return (
                      <li key={post.id}>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="block group"
                        >
                          <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 line-clamp-2">
                            {post.title}
                          </p>
                          {date && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {date.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                        </Link>
                      </li>
                    )
                  })}
                  {recentPosts.length === 0 && (
                    <li className="text-sm text-gray-500 dark:text-gray-400">
                      No posts yet.
                    </li>
                  )}
                </ul>
              </section>

              <section className="glass-card rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                  Tags
                </h3>
                {allTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-300"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tags will appear as you publish posts.
                  </p>
                )}
              </section>
            </aside>
          </div>
        )}
      </main>

      <PublicFooter />
    </div>
  )
}

